<template>
  <Loader style="z-index: 1000;" v-if="loader" />

  <div class="card">
    <div class="flex align-items-center gap-2 mb-4">
      <p class="text-xl m-0">SWITCH-OUT CREDITO</p>
    </div>

    <div class="flex align-items-center mb-4 gap-4">
      <Calendar v-model="selectedDate" :showIcon="true" dateFormat="dd/mm/yy" placeholder="Seleziona Data" class="flex-shrink-0" />
      <div class="flex gap-3">
        <Button :class="{'p-button-outlined mr-3 ml-3 ': selectedTypeFornitura !== 'POD'}" @click="selectType('POD')">POD</Button>
        <Button :class="{'p-button-outlined ml-3': selectedTypeFornitura !== 'PDR'}" @click="selectType('PDR')">PDR</Button>
      </div>
    </div>

    <div class="flex align-items-center gap-4 mb-3 p-2 border-round">
      <span class="font-bold">Legenda stati:</span>
      <div class="flex align-items-center gap-4 ml-2">
        <div class="flex align-items-center gap-2">
          <div class="status-in-sospensione legend-box mr-1"></div>
          <span>Stato di sospensione</span>
        </div>
        <div class="flex align-items-center gap-2 ml-2">
          <div class="status-richiesta legend-box mr-1"></div>
          <span>Contabile Presente</span>
        </div>
      </div>
    </div>
    
    <div class="overflow-x-auto">
      <DataTable 
        ref="dt_swout_credito"
        :value="switchoutData" 
        :rowClass="getRowClass"
        tableStyle="width: 100%"
        dataKey="fornitura"
        responsiveLayout="scroll"
        v-model:filters="filters"
        :paginator="true" 
        :rows="100"
        :rowsPerPageOptions="[10, 20, 50, 100, 500]"
        currentPageReportTemplate="{first} a {last} di {totalRecords}"
        paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
        exportFilename="SWITCH-OUT-CREDITO"
        csvSeparator=";"
      >
        <template #header>
          <div class="flex justify-content-between flex-wrap">
            <div class="flex align-items-center justify-content-center mb-2">
              <span class="p-input-icon-left">
                <i class="pi pi-search" />
                <InputText v-model="filters['global'].value" placeholder="Cerca..." />
              </span>
            </div>
          </div>
        </template>
        <template #empty>
          <div class="text-center">Nessun dato trovato.</div>
        </template>

        <Column field="agente_firmatario" header="Agente" :sortable="true" />
        <Column field="scala_agenti" header="Scala Agenti" :sortable="true" />
        <Column field="rappresentante" header="Rappresentante" :sortable="true" />
        <Column field="cliente" header="Cliente" :sortable="true" />
        <Column field="fornitura" header="Fornitura" :sortable="true">
          <template #body="{ data }">
            {{ data.fornitura }}
          </template>
        </Column>
        <Column field="numero_contabilita" header="Numero Fattura" :sortable="true">
          <template #body="{ data }">
            <div v-if="data.numero_contabilita" class="flex flex-column">
              <span v-for="(numero, index) in String(data.numero_contabilita).split(',')" :key="index" class="mb-1 font-bold">
                {{ numero }}
              </span>
            </div>
          </template>
        </Column>
        <Column field="data_emissione" header="Data Emissione" :sortable="true">
          <template #body="{ data }">
            <div v-if="data.data_emissione" class="flex flex-column">
              <span v-for="data_em in [...new Set(String(data.data_emissione).split(','))]" :key="data_em">
                {{ formatDate(data_em) }}
              </span>
            </div>
          </template>
        </Column>
        <Column field="data_scadenza" header="Data Scadenza" :sortable="true">
          <template #body="{ data }">
            <div v-if="data.data_scadenza" class="flex flex-column">
              <span v-for="data_sc in [...new Set(String(data.data_scadenza).split(','))]" :key="data_sc">
                {{ formatDate(data_sc) }}
              </span>
            </div>
          </template>
        </Column>
        <Column field="cmor" header="CMOR" :sortable="true">
          <template #body="{ data }">
            <div v-if="data.cmor" class="flex flex-column">
              <span v-for="cmor in [...new Set(String(data.cmor).split(','))]" :key="cmor">
                {{ cmor }}
              </span>
            </div>
          </template>
        </Column>
        <Column field="name_stato_fornitura" header="Stato Fornitura" :sortable="true" />
        <Column field="is_point" header="Point" :sortable="true">
          <template #body="{ data }">
            {{ Boolean(data.is_point) ? data.agente_firmatario : "No" }}
          </template>
        </Column>
        <Column field="type_periodo" header="Fatturazione" :sortable="true" />
        <Column field="type_pay" header="Pagamento" :sortable="true" />
        <Column field="totale_fattura" header="Tot.Fattura" :sortable="true">
          <template #body="{ data }">
            <div v-if="data.totale_fattura" class="flex flex-column">
              <span v-for="(totale, index) in String(data.totale_fattura).split(',')" :key="index" 
                    class="mb-1 border-bottom-1 border-gray-200">
                {{ Number(totale).toFixed(2) }}€
              </span>
            </div>
          </template>
        </Column>
        <Column field="totale_pagato_contabilita" header="Tot.Pagato" :sortable="true">
          <template #body="{ data }">
            <div v-if="data.totale_pagato_contabilita" class="flex flex-column">
              <span v-for="(totale_pagato, index) in String(data.totale_pagato_contabilita).split(',')" :key="index" 
                    class="mb-1 border-bottom-1 border-gray-200">
                {{ Number(totale_pagato).toFixed(2) }}€
              </span>
            </div>
          </template>
        </Column>
        <Column field="saldo_contabilita" header="Saldo Fattura" :sortable="true">
          <template #body="{ data }">
            <div v-if="data.numero_contabilita" class="flex flex-column">
              <span v-for="(saldo, index) in String(data.saldo_contabilita === null ? '0' : data.saldo_contabilita).split(',')" :key="index" 
                    :class="['mb-1 border-bottom-1 border-gray-200', { 'text-green-500': Number(saldo) === 0 }]">
                {{ Number(saldo).toFixed(2) }}€
              </span>
            </div>
          </template>
        </Column>
        <Column field="importo_contabili" header="Importo Totale Contabile" :sortable="true">
          <template #body="{ data }">
            <div v-if="data.importo_contabili && Number(String(data.importo_contabili).split(',')[0]) > 0" class="text-yellow-500">
              {{ Number(String(data.importo_contabili).split(',')[0]).toFixed(2) }}€
            </div>
          </template>
        </Column>
        <Column field="credito_cliente" header="Credito Cliente" :sortable="true">
          <template #body="{ data }">
            <span v-if="data.credito_cliente && Number(data.credito_cliente) !== 0">
              {{ Number(data.credito_cliente).toFixed(2) }}€
            </span>
          </template>
        </Column>
        <Column field="data_invio_preavviso" header="Data Invio Preavviso" :sortable="true" />
        <Column field="data_invio_storno" header="Data Invio Storno" :sortable="true" />
        <Column field="data_invio_sollecito" header="Data Invio Sollecito" :sortable="true" />
        <Column header="Sposta in Preview" :sortable="false">
          <template #body="{ data }">
            <Button
              :icon="isButtonDisabled(data.id_fornitura) ? 'pi pi-check' : 'pi pi-eye'"
              @click="addToPreview(data)"
              :disabled="isButtonDisabled(data.id_fornitura)"
              :class="[
                'p-button-sm',
                isButtonDisabled(data.id_fornitura) ? 'p-button-success' : 'p-button-secondary'
              ]"
              v-tooltip="isButtonDisabled(data.id_fornitura) ? 'Già in preview' : 'Sposta in preview'"
            />
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script>
import Loader from "@/components/Loader.vue";
import { ref, getCurrentInstance } from "vue";
import Service from "@/services/ask-pratiche/pratiche-service";
import ServiceOperation from "@/services/ask-operation/operation-service";
import { FilterMatchMode } from 'primevue/api';
import moment from "moment";

export default {
  components: {
    Loader
  },
  setup() {
    const dt_swout_credito = ref();
    const self = getCurrentInstance();
    self.$eventBus = self.appContext.config.globalProperties.$eventBus;
    self.$http = self.appContext.config.globalProperties.$http;
    const service = new Service(self.$http, self.$eventBus);
    const serviceOperation = new ServiceOperation(self.$http, self.$eventBus);
    const loader = ref(false);
    const switchoutData = ref([]);
    const selectedTypeFornitura = ref('');
    const selectedDate = ref(new Date());
    const filters = ref({
      global: { value: null, matchMode: FilterMatchMode.CONTAINS }
    });
    const buttonStates = ref({});
    const clickedButtons = ref(new Set());

    const formatDate = (date) => {
      if (!date) return '';
      return moment(date).format('DD/MM/YYYY');
    };

    const isButtonDisabled = (id) => {
      return clickedButtons.value.has(id) || buttonStates.value[id];
    };

    const getRowClass = (data) => {
      if (data.name_stato_fornitura && 
          (data.name_stato_fornitura.toLowerCase().includes('sospension') || 
           data.name_stato_fornitura.toLowerCase().includes('sospeso'))) {
        return 'status-sospensione';
      }
      
      switch (data.name_stato_fornitura) {
        case 'In Sospensione':
          return 'status-in-sospensione';
        case 'Sospeso':
          return 'status-sospeso';
        case 'Sospensione richiesta':
          return 'status-richiesta';
        case 'Sospensione in lavorazione':
          return 'status-in-lavorazione';
        default:
          return '';
      }
    };

    const loadData = () => {
      if (!selectedTypeFornitura.value) return;
      
      loader.value = true;
      const tipoFornitura = selectedTypeFornitura.value === 'POD' ? 'EE' : 'GAS';
      const currentDate = moment(selectedDate.value).format('YYYY-MM-DD');
      
      service.getDataForEmailOrFrontendSwoutCredito(tipoFornitura, currentDate)
        .then(response => {
          switchoutData.value = response.items;
        })
        .catch(error => {
          console.error("Errore nel caricamento dei dati:", error);
          switchoutData.value = [];
        })
        .finally(() => {
          loader.value = false;
        });
    };


    const selectType = (type) => {
      selectedTypeFornitura.value = type;
      loadData();
    };


    const addToPreview = async (data) => {
      loader.value = true;
      try {
        clickedButtons.value.add(data.id_fornitura);
        
        const isPod = selectedTypeFornitura.value === 'POD';
        const typeFornitura = isPod ? 'pods' : 'pdrs';
        
        const response = await serviceOperation.createMultiPreSuspensionOrReactivation(
          [{ id: data.id_fornitura }],
          typeFornitura,
          'sospensioni'
        );

        if (response) {
          buttonStates.value[data.id_fornitura] = true;
          
          self.$toast.add({
            severity: 'success',
            summary: 'Successo',
            detail: '1 fornitura/e aggiunta in preview'
          });

          setTimeout(() => {
            loadData();
          }, 100);
        }
      } catch (error) {
        clickedButtons.value.delete(data.id_fornitura);
        
        console.error('Errore:', error);
        self.$toast.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Errore durante l\'aggiunta alla preview'
        });
      } finally {
        loader.value = false;
      }
    };

    return {
      dt_swout_credito,
      loader,
      switchoutData,
      formatDate,
      loadData,
      selectType,
      selectedTypeFornitura,
      selectedDate,
      filters,
      getRowClass,
      addToPreview,
      isButtonDisabled
    };
  }
}
</script>

<style scoped>
.status-sospensione {
  background-color: #ffedd5 !important; 
}

.status-in-sospensione {
  background-color: #ffe4c4 !important; 
}

.status-sospeso {
  background-color: #ffe6e6 !important; 
}

.status-richiesta {
  background-color: #e6ffe6 !important; 
}

.status-in-lavorazione {
  background-color: #f3e6ff !important; 
}

.p-datatable .p-datatable-tbody > tr.status-sospensione:hover {
  background-color: #fed7aa !important; 
}

.p-datatable .p-datatable-tbody > tr.status-in-sospensione:hover {
  background-color: #ffe4c4 !important;
}

.p-datatable .p-datatable-tbody > tr.status-sospeso:hover {
  background-color: #ffe6e6 !important;
}

.p-datatable .p-datatable-tbody > tr.status-richiesta:hover {
  background-color: #e6ffe6 !important;
}

.p-datatable .p-datatable-tbody > tr.status-in-lavorazione:hover {
  background-color: #f3e6ff !important;
}

.legend-box {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  border: 1px solid #dee2e6;
}

.p-datatable .p-datatable-tbody > tr > td {
  vertical-align: top;
  padding: 0.5rem;
}

.flex-column > span {
  margin-bottom: 0.2rem;
}

.flex-column > span:last-child {
  margin-bottom: 0;
}
</style>